import { type AdditionalDocumentDeliveryStatus, applicationsApi } from '@/api/applications';
import { notifySessionActivity } from '@/auth/sessionTimer';

import type { UploadedFilesStore } from './uploadedFiles';

/** Poll cadence. Delivery is an SFTP write of an already-prepared file — seconds, not minutes. */
const POLL_INTERVAL_MS = 2_000;

/**
 * How long to wait before giving up.
 *
 * ADR-0026 budgeted roughly three minutes here because conversion — including a possible
 * LibreOffice cold start — ran inside the delivery worker. It no longer does: conversion fires off
 * the malware verdict, long before the advisor gets here, so by this point the PDF already exists
 * and the worker is a plain zip Lambda doing a read, an encrypt and an upload. On that reasoning
 * the budget was cut to a minute.
 *
 * Raised to five (2026-10-02, the user's call) because what hangs off this wait changed. It used
 * to gate a Next — time out and the advisor loses a page transition they can repeat immediately.
 * It now sits in the MIDDLE of the submit chain (FormShell's `submitApplication`): a timeout there
 * abandons a press that has already saved, and the advisor has to run the whole thing again.
 * Giving up early on a worker that was merely slow is the more expensive mistake of the two, and
 * nothing is lost by waiting — the advisor is already held by the processing modal, and a failure
 * still reports the instant a file comes back `failed`, which is the only terminal verdict.
 */
const POLL_BUDGET_MS = 300_000;

export type DeliveryOutcome =
  | { ok: true }
  /** Something is wrong with a specific file, or the wait ran out. The advisor stays put. */
  | { ok: false; reason: 'failed' | 'timedOut' | 'error' };

/** One uploaded file, with everything the server needs to name it for BMO. */
export type DeliverableFile = {
  key: string;
  /** The originator entry this file fills; absent for the free-form optional bucket. */
  entryId?: string;
  /** What the advisor typed beside it. The DOCNAME of a bucket file comes from this. */
  description?: string;
  sequenceInPackage?: number;
};

/**
 * Every uploaded file on the advisor's store, named entries and optional bucket alike, paired
 * with what the server needs to compose its §14.1 name.
 *
 * <p>
 * The descriptions are read HERE, at the moment of delivery, rather than having been captured at
 * upload — which is the whole point of moving the rename. Whatever the advisor settled on while
 * they were on the step is what BMO files the document under.
 * </p>
 */
export function collectDeliverableFiles(store: UploadedFilesStore): DeliverableFile[] {
  const candidates: Array<Omit<DeliverableFile, 'key'> & { key: string | undefined }> = [
    ...Object.entries(store.entries).flatMap(([entryId, entryFiles]) =>
      entryFiles.map((f) => ({
        key: f.s3Key,
        entryId,
        sequenceInPackage: f.sequenceInPackage,
      })),
    ),
    ...store.bucket.map((f) => ({
      key: f.s3Key,
      description: f.description,
      sequenceInPackage: f.sequenceInPackage,
    })),
  ];

  // Deduped on key, like the key list this replaced: the same object must not be booked twice in
  // one call, and the ledger row is per key. A file with no key never reached S3 — a preview
  // pick, or an upload still in flight.
  const seen = new Set<string>();
  const files: DeliverableFile[] = [];
  for (const candidate of candidates) {
    if (!candidate.key || seen.has(candidate.key)) continue;
    seen.add(candidate.key);
    files.push({ ...candidate, key: candidate.key });
  }
  return files;
}

/** The keys alone, for the status poll — which identifies rows by key and nothing else. */
export function keysOf(files: readonly DeliverableFile[]): string[] {
  return files.map((f) => f.key);
}

/**
 * Delivers the advisor's additional files to BMO and waits for confirmation (ADR-0026).
 *
 * <h3>Why this blocks the advance at all</h3>
 * Delivery is asynchronous for reliability — retry, backoff, dead-letter queue and alerting are
 * things an in-request SFTP call cannot provide, and a multi-file SFTP session does not reliably
 * fit inside API Gateway's 29-second ceiling. But an advisor must not be able to reach submit
 * having left documents undelivered, so Continue stays blocked until every file is confirmed.
 * Those two are not in tension: the queue buys reliability, the poll buys a fail-closed gate.
 *
 * <h3>Ordering</h3>
 * The caller MUST have saved successfully first. Spec §14 is explicit that BMO acts on the
 * documents we SFTP, not on the JSON save — the save establishes the record they attach to, so a
 * failed save must never reach here.
 */
export async function deliverAndConfirm(args: {
  applicationId: string;
  templateId: string;
  /**
   * The branched-in workflow, when one fired. The server resolves each file's `entryId` against
   * it — the entry's label is §14.1's DOCNAME and its `isRequired` is the MANDATORY|OPTIONAL
   * field, and both live on the branch target rather than on the base form.
   *
   * Not sent on the status poll below: that one matches ledger rows by key, and a key is owned by
   * the base form.
   */
  subsequentTemplateId?: string | null;
  files: DeliverableFile[];
  /**
   * BMO's creation timestamp for this application. The server's FALLBACK instant for §14.1 names
   * when `deliveryTimestamp` is absent — still never a clock read, because the save that just ran
   * has to land on the same answer.
   */
  creationTimestamp?: string;
  /**
   * The instant of the press that triggered this, minted ONCE by the caller and already sent on
   * the save that preceded it. That sharing is the whole point: the save promised BMO a set of
   * §14.1 names, this call recomposes them, and BMO matches deliveries purely by name — so two
   * reads of a moving clock would promise one document and deliver another.
   */
  deliveryTimestamp?: string;
  /** Injected in tests; real callers get the default. */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<DeliveryOutcome> {
  const { applicationId, templateId, subsequentTemplateId, files, creationTimestamp } = args;
  const { deliveryTimestamp } = args;
  const now = args.now ?? (() => Date.now());
  const sleep = args.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const keys = keysOf(files);

  // Nothing uploaded on this step — a legitimate case, since the originator may have made every
  // entry optional.
  if (keys.length === 0) return { ok: true };

  // An advisor waiting on delivery shouldn't cross the idle-refresh boundary (ADR-0022).
  notifySessionActivity();

  try {
    await applicationsApi.deliverAdditionalDocuments({
      applicationId,
      templateId,
      subsequentTemplateId,
      files,
      creationTimestamp,
      deliveryTimestamp,
    });
  } catch {
    // Includes FILE_NOT_READY, which means the advisor got here before a file finished
    // converting, and DUPLICATE_DOCUMENT_NAME, which means two descriptions fold to one §14.1
    // name. The step's inline check should have caught the second where it could still be fixed,
    // so both are worth surfacing rather than silently retrying.
    return { ok: false, reason: 'error' };
  }

  const deadline = now() + POLL_BUDGET_MS;

  for (;;) {
    let results: AdditionalDocumentDeliveryStatus[];
    try {
      results = await applicationsApi.getDeliveryStatus({ applicationId, templateId, keys });
    } catch {
      // Transient. Keep polling until the budget runs out rather than failing the advance on one
      // dropped request.
      results = [];
    }

    const byKey = new Map(results.map((r) => [r.key, r.status]));

    if (results.length > 0 && keys.every((k) => byKey.get(k) === 'delivered')) {
      return { ok: true };
    }

    // A `failed` row is terminal — the queue already exhausted its retries and the message is in
    // the dead-letter queue. Waiting out the rest of the budget would tell the advisor nothing.
    if (keys.some((k) => byKey.get(k) === 'failed')) {
      return { ok: false, reason: 'failed' };
    }

    if (now() >= deadline) {
      return { ok: false, reason: 'timedOut' };
    }

    await sleep(POLL_INTERVAL_MS);
    notifySessionActivity();
  }
}
